import { record } from '@/api/record'
import {
  AlertIconName,
  alertIcons,
  IconName,
  icons,
  PosterName,
  posters
} from '@/data/images'
import newworks from '@/data/newworks'
import { ClickAction, report_descriptions } from '@/data/report'
import { query } from '@/utils/device'
import {
  getAlertIcon,
  getIcon,
  getStackingPosterImage,
  getSwiperItemCover
} from '@/utils/image'
import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'

type SwiperLocation = {
  key: string
  index: number
  displayIndex: number
  isBanner: boolean
}

export const namedImages = {} as Record<
  IconName | AlertIconName | PosterName,
  string
>

const formatQuery = () => {
  console.log('[[ ConfAndQuery ] Init Store ]', query)
  const { channel, tag, openid, unionid, skuid } = query
  const set = query.set || query.reset

  const base = { channel, tag, openid, unionid }
  if (typeof set === 'string' && set !== '') {
    const path = set.split('$').map(s => s.toUpperCase())
    if (path[0] === 'ALLWORKS') {
      if (path[1] && typeof skuid === 'string' && skuid !== '') {
        return {
          ...base,
          skuid: skuid.toUpperCase(),
          tab: 'ALLWORKS',
          set: [path[1]]
        }
      } else {
        return {
          ...base,
          skuid: null,
          tab: 'ALLWORKS',
          set: []
        }
      }
    } else {
      if (path[0]) {
        return {
          ...base,
          skuid:
            typeof skuid === 'string' && skuid !== ''
              ? skuid.toUpperCase()
              : null,
          tab: 'NEWWORKS',
          set: [...path]
        }
      }
    }
  }
  return {
    ...base,
    skuid: null,
    tab: 'NEWWORKS',
    set: []
  }
}

const getDefaultLocation = () => {
  const index = newworks.findIndex(works => works.hidden !== true)
  return {
    index,
    displayIndex: 0,
    key: newworks[index]?.key || ''
  }
}

const sets: string[] = []
const images: string[] = []

;(() => {
  newworks.forEach(item => {
    if (item.type === 'categories') {
      item.categories.forEach(cat => {
        if (cat.cover) {
          cat.kv = getSwiperItemCover(cat.cover, 'webp')
          images.push(cat.kv)
        }
        sets.push(`${item.key}$${cat.value}`)
      })
    } else if (item.type === 'series') {
      item.series.forEach(serie => {
        if (serie.icon) {
          images.push(serie.icon)
        }
        sets.push(`${item.key}$${serie.skuId}`)
      })
    } else {
      if (item.cover) {
        item.kv = getSwiperItemCover(item.cover)
        images.push(item.kv)
      }
      sets.push(item.key)
    }
  })
})()
;(() => {
  ;(Object.keys(icons) as IconName[]).forEach(key => {
    const [name, suffix] = icons[key]
    namedImages[key] = getIcon(name, suffix)
    images.push(namedImages[key])
  })
  ;(Object.keys(alertIcons) as AlertIconName[]).forEach(key => {
    namedImages[key] = getAlertIcon(alertIcons[key])
    images.push(namedImages[key])
  })
  ;(Object.keys(posters) as PosterName[]).forEach(key => {
    namedImages[key] = getStackingPosterImage(posters[key][0], posters[key][1])
    images.push(namedImages[key])
  })
})()

export const useConfAndQueryStore = defineStore('conf-and-query', () => {
  const channel = ref('')
  const tag = ref('')
  const openId = ref('')
  const unionId = ref('')

  const firstClass = reactive<SwiperLocation>({
    key: '',
    index: -1,
    displayIndex: -1,
    isBanner: true
  })
  const secondClass = reactive({ index: 1 })
  const toSelect = reactive<
    | {
        fromAllWorks: false
        item: string | null
      }
    | {
        fromAllWorks: true
        cat: string
        item: string | null
      }
  >({ fromAllWorks: false, item: null })

  const report = (
    actionNo: ClickAction,
    actionValue: string | string[] = ''
  ) => {
    const params = {
      actionNo,
      actionValue:
        typeof actionValue === 'object' ? actionValue.join('') : actionValue,
      source: 'h5',
      actionType: 2,
      description: report_descriptions[actionNo] || '',
      openId: openId.value,
      unionId: unionId.value,
      tag: tag.value,
      scene: channel.value
    }

    record(params)
      .then(() => console.log('[[ Record ]]', params))
      .catch(error =>
        console.error('[[ Record ] Failed ]', params, {
          reason: error?.message || String(error) || ''
        })
      )
  }

  ;(() => {
    const { tab, set, skuid, ...base } = formatQuery()
    channel.value = base.channel || 'default'
    tag.value = base.tag || ''
    openId.value = base.openid || ''
    unionId.value = base.unionid || ''
    // console.log('[[ ConfAndQuery ] FormattedResult ]', {
    //   base,
    //   tab,
    //   set,
    //   skuid
    // })

    if (tab === 'ALLWORKS') {
      Object.assign(firstClass, getDefaultLocation())
      Object.assign(toSelect, {
        fromAllWorks: true,
        cat: set[0] || '',
        item: skuid
      })
    } else {
      if (set.length) {
        let displayIndex = 0
        newworks.some((item, index: number) => {
          if (item.key === set[0]) {
            if (item.hidden) {
              Object.assign(firstClass, {
                index: index,
                displayIndex: 0,
                key: set[0],
                isBanner: false
              })
              return true
            }
            Object.assign(firstClass, {
              index: index,
              displayIndex,
              key: set[0],
              isBanner: true
            })
            if (set[1]) {
              firstClass.isBanner = false
              if (item.type === 'categories') {
                item.categories.some((subItem, subIndex: number) => {
                  if (subItem.value === set[1]) {
                    secondClass.index = subIndex
                  }
                })
              } else if (item.type === 'series') {
                item.series.some((subItem, subIndex: number) => {
                  if (subItem.skuId === set[1]) {
                    secondClass.index = subIndex
                  }
                })
              }
            }
            return true
          }
          displayIndex += item.hidden ? 0 : 1
          return false
        })
      } else {
        Object.assign(firstClass, getDefaultLocation())
      }
      Object.assign(toSelect, {
        fromAllWorks: false,
        item: skuid
      })
    }
  })()
  return {
    images: images,
    channel,
    firstClass,
    secondClass,
    toSelect,
    works: newworks,
    slides: newworks.filter(slide => !slide.hidden),
    report
  }
})
